import moment from "moment";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { sendTransactionEmail } from "../lib/Email";

const PRODUCT_NAME = process.env.REACT_APP_PRODUCT_NAME;

/**
 * Fetch plan metadata from Firestore.
 * Returns an array like: [{ key: "plan1", name, amount, duration, profit, maxTps, ... }, ...]
 */
export const fetchPlans = async () => {
  try {
    const docRef = doc(db, "users", "plans");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const plansData = docSnap.data(); // e.g., { plan1: {...}, plan2: {...} }
      return Object.entries(plansData).map(([key, plan]) => ({
        key,
        ...plan,
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error fetching plans:", error);
    return [];
  }
};

/**
 * Fetch user role, data, and active trading transactions.
 */
export const fetchUserData = async (user) => {
  if (!user?.uid) return null;
  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return null;

  const ud = snap.data();
  const activeTransactions = (ud.transactions || [])
    .filter((t) => t.account === "Trading" && t.stage?.toLowerCase() === "active")
    .slice(0, 1);

  return {
    role: ud.role,
    userData: ud,
    activeTransactions,
  };
};

/**
 * Compute profit distribution based on invested amount and plan.
 */
export const calculateProfitDistribution = (amount, plan) => {
  const profitPercentage = plan.profit / 100;
  const tps = plan.maxTps;
  let totalProfit = 0;

  switch (plan.duration) {
    case 1:
      totalProfit = Math.floor(amount + ((tps * profitPercentage) * 2));
      break;
    case 3:
      totalProfit = Math.floor(amount + ((tps * profitPercentage) * 2));
      break;
    case 5:
      totalProfit = Math.floor(amount + ((tps * profitPercentage) * 2));
      break;
    case 7:
      totalProfit = Math.floor(amount * 45);
      break;
    default:
      totalProfit = 0;
  }

  const now = moment();
  let percentages = [];

  if (plan.duration === 1) percentages = [100];
  else if (plan.duration === 3) percentages = [30, 40, 30];
  else if (plan.duration === 5) percentages = [20, 10, 10, 30, 30];
  else if (plan.duration === 7) percentages = [20, 10, 10, 20, 10, 10, 20];

  const distributions = percentages.map((percent, idx) => ({
    amount: Math.floor(totalProfit * (percent / 100)),
    date: now.clone().add(idx + 1, "days").format("MMMM D, YYYY"),
    time: "09:00:00",
    scheduled: now.clone().add(idx + 1, "days").toISOString(),
    percent,
    stage: "pending",
  }));

  return {
    totalProfit,
    distributions,
    finalAmount: amount + totalProfit,
    lastPaymentDate: distributions[distributions.length - 1]?.scheduled,
  };
};

/**
 * Handle starting a new trading investment for the user.
 */
export const handleStartTrade = async ({
  user,
  selectedPlan,
  investmentAmount,
  accountBalances,
  setSuccess,
  setError,
  navigate,
}) => {
  const planList = await fetchPlans();
  const plan = planList.find((p) => p.name === selectedPlan);
  if (!plan) {
    setError("Selected plan not found");
    return;
  }

  const amount = parseFloat(investmentAmount);
  const now = moment();
  const userRef = doc(db, "users", user.uid);
  const profitDetails = calculateProfitDistribution(amount, plan);

  const investmentTxn = {
    account: "Investments",
    amount,
    beneficiary: PRODUCT_NAME,
    date: now.format("MMMM D, YYYY"),
    time: now.format("HH:mm:ss"),
    description: `Investment in ${plan.name}`,
    type: "debit",
    stage: "complete",
  };

  const tradingTxn = {
    account: "Trading",
    amount,
    beneficiary: user.email,
    date: now.format("MMMM D, YYYY"),
    time: now.format("HH:mm:ss"),
    description: `Profits from ${plan.name}`,
    type: "credit",
    stage: "active",
    planDetails: {
      name: plan.name,
      initialAmount: amount,
      duration: plan.duration,
      totalProfit: profitDetails.totalProfit,
    },
    profitSchedule: profitDetails.distributions,
  };

  await updateDoc(userRef, {
    "accountBalances.Investments": (accountBalances.Investments || 0) - amount,
    "accountBalances.Trading": (accountBalances.Trading || 0) + amount + profitDetails.totalProfit,
    role: "investor",
    transactions: arrayUnion(investmentTxn, tradingTxn),
    activeInvestments: arrayUnion({
      plan: plan.name,
      amount,
      startDate: now.toISOString(),
      profitSchedule: profitDetails.distributions,
      stage: "active",
    }),
  });

  setSuccess(`Investment completed! Total profit: $${profitDetails.totalProfit}`);
  setTimeout(() => {
    navigate("/mega-fxt");
    window.location.reload();
  }, 1000);
};

/**
 * Handle user profit claim/withdrawal for today's distribution.
 */
export const handleClaimWithdrawal = async ({
  user,
  transaction,
  accountBalances,
  setSuccess,
  setError,
  navigate,
}) => {
  const now = moment();
  const today = now.format("MMMM D, YYYY");

  const scheduleToClaim = transaction.profitSchedule?.find(
    (s) => moment(s.date).format("MMMM D, YYYY") === today
  );
  if (!scheduleToClaim) {
    return setError("No profit available to claim today.");
  }

  const userRef = doc(db, "users", user.uid);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.data();
  const { fullName, accountNumber } = userData;

  const claimTxn = {
    account: "Trading",
    amount: scheduleToClaim.amount,
    beneficiary: PRODUCT_NAME,
    date: now.format("MMMM D, YYYY"),
    time: now.format("HH:mm:ss"),
    description: `Profit claim from ${transaction.planDetails?.name}`,
    type: "credit",
    stage: "pending",
    relatedTrade: transaction.planDetails?.name,
  };

  const updatedProfitSchedule = transaction.profitSchedule.filter(
    (s) => s.scheduled !== scheduleToClaim.scheduled
  );
  const isLastPayment = updatedProfitSchedule.length === 0;

  let updatedTransactions = (userData.transactions || []).filter(
    (t) => !(t.account === "Trading" && t.stage === "active" && t.description === transaction.description)
  );

  updatedTransactions.push({ ...transaction, profitSchedule: updatedProfitSchedule });
  updatedTransactions.push(claimTxn);

  const updateData = {
    "accountBalances.Investments": (accountBalances.Investments || 0) + scheduleToClaim.amount,
    "accountBalances.Trading": (accountBalances.Trading || 0) - scheduleToClaim.amount,
    transactions: updatedTransactions,
  };

  if (isLastPayment) {
    updateData.role = "user";

    const profitTxn = {
      account: "Investments",
      amount: transaction.planDetails.totalProfit,
      beneficiary: user.email,
      date: now.format("MMMM D, YYYY"),
      time: now.format("HH:mm:ss"),
      description: `Total profit from ${transaction.planDetails?.name} plan`,
      type: "credit",
      stage: "complete",
    };

    updatedTransactions = updatedTransactions.filter(
      (t) =>
        !(t.account === "Trading") &&
        !(t.account === "Investments" && t.description === `Investment in ${transaction.planDetails?.name}`)
    );

    updateData.activeInvestments = arrayRemove({
      plan: transaction.planDetails?.name,
      amount: transaction.amount,
      startDate: transaction.date,
      profitSchedule: transaction.profitSchedule,
      status: "active",
    });

    updatedTransactions.push(profitTxn);
    updateData.transactions = updatedTransactions;
  }

  await updateDoc(userRef, updateData);

  await sendTransactionEmail(
    user.email,
    accountNumber,
    fullName,
    { ...claimTxn, planDetails: transaction.planDetails }
  );

  setSuccess(`Successfully claimed $${scheduleToClaim.amount} profit!`);
  setTimeout(() => {
    navigate("/mega-fxt");
    window.location.reload();
  }, 1000);
};
